import type { FC, RefObject } from "react";
import { useMemo } from "react";

import { useTranslations } from "@ui/providers/useTranslations";
import classNames from "classnames";

import { DeviceDefaultIcon } from "@jti/brand-icons/device-default";
import { StoreLocatorIcon } from "@jti/brand-icons/store-locator";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

import { type AccountDropdownSettings } from "../../Header";
import { HeaderToolsItem } from "../HeaderToolsItem";
import { SearchHeaderButton } from "../SearchHeaderButton/SearchHeaderButton";
import { UserActions } from "../UserActions/UserActions";

import styles from "./Tools.module.scss";

export type ToolsProps = {
  accountLink?: string;
  accountLabel?: string;
  accountLoadingLabel?: string;
  accountDropdownSettings?: AccountDropdownSettings;
  storeLocatorLink?: string;
  storeLocatorLabel?: string;
  openMenuLabel?: string;
  closeMenuLabel?: string;
  navAriaLabel?: string;
  languageLabel?: string;
  searchLabel?: string;
  mobileAppLink?: string;
  resultsLink?: string;
  onOpenSearch: () => void;
  onCloseSearch: () => void;
  isSearchOpened: boolean;
  renderMiniCart?: () => JSX.Element;
  withCart?: boolean;
  welcomeText?: string;
  onLogOut: () => void;
  isEnabledUserIndication?: boolean;
  isEnabledUserNameForIndication?: boolean;
  currentPathname?: string;
  userDialogPortalRef?: RefObject<HTMLElement | null>;
};

export const Tools: FC<ToolsProps> = ({
  accountLink,
  accountLabel,
  accountLoadingLabel,
  accountDropdownSettings,
  mobileAppLink,
  storeLocatorLink,
  storeLocatorLabel,
  resultsLink,
  isSearchOpened,
  renderMiniCart,
  withCart,
  onCloseSearch,
  onOpenSearch,
  onLogOut,
  isEnabledUserIndication,
  isEnabledUserNameForIndication,
  currentPathname: currentPathnameProp,
  userDialogPortalRef,
}) => {
  const { __ } = useTranslations();
  const currentPathname = useMemo(
    () => currentPathnameProp ?? (typeof window !== "undefined" ? window.location.pathname : ""),
    [currentPathnameProp]
  );
  return (
    <div className={styles.tools}>
      <ul className={styles.list}>
        {resultsLink && (
          <li>
            <SearchHeaderButton isOpened={isSearchOpened} onClose={onCloseSearch} onOpen={onOpenSearch} />
          </li>
        )}

        {accountLink && (
          <li className={styles.userButtonContainer}>
            <UserActions
              accountLink={accountLink}
              accountLabel={accountLabel}
              accountLoadingLabel={accountLoadingLabel}
              accountDropdownSettings={accountDropdownSettings}
              onLogOut={onLogOut}
              isEnabledUserIndication={isEnabledUserIndication}
              isEnabledUserNameForIndication={isEnabledUserNameForIndication}
              userDialogPortalRef={userDialogPortalRef}
            />
          </li>
        )}

        {withCart && renderMiniCart && (
          <li>
            <div data-testid="miniCart" data-cart-mini-container>
              {renderMiniCart()}
            </div>
          </li>
        )}

        {storeLocatorLink && (
          <li>
            <HeaderToolsItem
              aria-label={storeLocatorLabel}
              title={storeLocatorLabel}
              href={storeLocatorLink}
              className={classNames({ [styles.isActive]: currentPathname.includes(storeLocatorLink) })}
              data-testid="headerStoreLocatorButton"
              onClick={() => dispatchTrackingEvent(TrackingEvents.TrackingStoreLocatorClick)}
              icon={StoreLocatorIcon}
            />
          </li>
        )}

        {mobileAppLink && (
          <li>
            <HeaderToolsItem
              href={mobileAppLink}
              className={classNames({ [styles.isActive]: currentPathname.includes(mobileAppLink) })}
              data-testid="mobileApp"
              aria-label={__("headerlinks.button.mobileapp")}
              icon={DeviceDefaultIcon}
            />
          </li>
        )}
      </ul>
    </div>
  );
};
